import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

type Props = {
  children: ReactNode;
  /** Mensagem quando der erro de render (ex.: histórico vs rastreamento) */
  fallbackMessage?: string;
  /** Chave opcional para reset controlado do boundary (ex.: nova busca/troca de aba). */
  resetKey?: string | number;
};

type State = { hasError: boolean; errorMessage?: string };

/**
 * Evita tela branca se algo quebrar ao renderizar resultados (dados grandes / bug pontual).
 */
export class VehicleTrackingBoundary extends Component<Props, State> {
  state: State = { hasError: false, errorMessage: undefined };

  static getDerivedStateFromError(error: Error): State {
    const msg = String(error?.message || '');
    // Hotfix mobile: alguns browsers disparam DOMException intermitente de removeChild
    // durante re-render/tab switch, sem comprometer dados da busca. Não derruba a seção.
    if (msg.includes("removeChild") && msg.includes("não é filho")) {
      return { hasError: false, errorMessage: undefined };
    }
    if (msg.includes("removeChild") && msg.includes("not a child")) {
      return { hasError: false, errorMessage: undefined };
    }
    return { hasError: true, errorMessage: undefined };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[DashboardSectionBoundary]', error, info.componentStack);
    this.setState({ errorMessage: error?.message || 'Erro de renderização' });
  }

  componentDidUpdate(prevProps: Props): void {
    if (!this.state.hasError) return;
    if (prevProps.resetKey !== this.props.resetKey) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ hasError: false });
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      const msg =
        this.props.fallbackMessage ||
        'Não foi possível exibir esta secção. Tente recarregar a página ou buscar de novo.';
      return (
        <Card className="bg-protec-dark-light border-protec-gold/20">
          <CardContent className="py-8 text-center space-y-4">
            <p className="text-white text-sm">{msg}</p>
            {this.state.errorMessage ? (
              <p className="text-xs text-rose-300/90 break-words max-w-[92%] mx-auto">
                Detalhe: {this.state.errorMessage}
              </p>
            ) : null}
            <Button
              type="button"
              className="bg-protec-gold text-black"
              onClick={() => this.setState({ hasError: false, errorMessage: undefined })}
            >
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      );
    }
    return this.props.children;
  }
}
